import { useEffect, useState } from "react";
import { Input, Form, Select, Divider } from "antd";
import { getOptionArray, getDataArray, selectFilterOption, modalFromLayout } from "@/lib/antdElementFunction";
import { getLangList } from '@/lib/i18n';
import { ReRangePicker, ReButton } from "@/components/refineElement";
import { getMoment, dateTimeFormat4, yearArray, getCurrentAcademicYear } from "@/lib/dateTime";
import { hasPermission } from "@/lib/permissionUtil";
import { getClassDataArray } from "@/lib/humanResourceManagement";
import SectionConfig from "@/config/section";
import SemesterConfig from "@/config/semester";
import { SemesterType } from "@/containers/organization/semesterManagement/types";

const { getClasses } = SectionConfig;
const { getSemestersByYear } = SemesterConfig;

interface Props {
  form?: any;
  classDataArray: Array<string>;
  placeInfo: any;
  handleType: string;
  selectedDate: any;
  clickDelete: any;
}

function ModalInfo(props: Props) {
  const { form, placeInfo, handleType, selectedDate, clickDelete } = props;
  const [classDataArray, setClassDataArray] = useState<{ label: string, value: number }[]>([]);
  const [semesterArray, setSemesterArray] = useState<SemesterType[]>([]);
  const rentOutFieldT: any = getLangList("rentOutField");
  const semesterT: any = getLangList("semester");
  const buttonT: any = getLangList("button");

  const fields = [
    { label: rentOutFieldT.THREE_LOBBY, value: 'threeLobby' },
    { label: rentOutFieldT.LIBRARY, value: 'library' },
    { label: rentOutFieldT.PERFORMANCE_HALL, value: 'performanceHall' },
  ];

  const getSemestersByYearFunction = async (year: number) => {
    return getSemestersByYear({ year }).then((res: any) => {
      const semesterArray = res?.value ?? [];

      setSemesterArray(semesterArray);
      setClassDataArray([]);
      form.setFieldsValue({ semesterId: null, classId: null });
    });
  };

  const getAllClassFunction = async (semesterId: number) => {
    return getClasses({ semesterId }).then((res: any) => {
      const allClassObject = res?.value ?? {};

      setClassDataArray(getClassDataArray(allClassObject));
      form.setFieldsValue({ classId: null });
    });
  };

  useEffect(() => {
    const { startTime, endTime, placeName, classId, description, year, semesterId } = placeInfo ?? {};

    const settingInitData = async () => {
      await getSemestersByYearFunction(year);
      await getAllClassFunction(semesterId);

      form.setFieldsValue({
        date: [getMoment(startTime), getMoment(endTime)],
        placeName,
        classId,
        description,
        year,
        semesterId,
      });
    }

    if (handleType === 'update') {
      settingInitData();
    }
    if (handleType === 'create') {
      const { year } = form.getFieldsValue();

      getSemestersByYearFunction(year);
      form.setFieldsValue({
        date: [
          getMoment(getMoment(selectedDate).startOf('day').format(dateTimeFormat4)),
          getMoment(getMoment(selectedDate).endOf('day').format(dateTimeFormat4)),
        ],
      });
    }
  }, [form, placeInfo, handleType]);

  return (
    <Form
      className="admin-form"
      {...modalFromLayout}
      form={form}
      initialValues={{
        year: getCurrentAcademicYear().toString(),
      }}
    >
      <Form.Item label={rentOutFieldT.PLACE} name="placeName" rules={[{ required: true }]}>
        <Select showSearch filterOption={(input: any, option: any) => selectFilterOption(input, option)}>
          {getOptionArray(fields)}
        </Select>
      </Form.Item>

      <Form.Item label={rentOutFieldT.TIME} name="date" rules={[{ required: true }]}>
        <ReRangePicker format='dateTimeFormat4' />
      </Form.Item>

      <Form.Item name="year" label={semesterT.YEAR} rules={[{ required: true }]}>
        <Select
          showSearch
          optionFilterProp="children"
          filterOption={(input: any, option: any) => selectFilterOption(input, option)}
          style={{ width: '200px' }}
          onChange={(year: number) => getSemestersByYearFunction(year)}
        >
          {getOptionArray(getDataArray(yearArray, '', ''))}
        </Select>
      </Form.Item>

      <Form.Item name="semesterId" label={semesterT.SEMESTER} rules={[{ required: true }]}>
        <Select
          showSearch
          optionFilterProp="children"
          filterOption={(input: any, option: any) => selectFilterOption(input, option)}
          style={{ width: '200px' }}
          onChange={getAllClassFunction}
        >
          {getOptionArray(getDataArray(semesterArray, 'semesterId', 'semesterName'))}
        </Select>
      </Form.Item>

      <Form.Item label={rentOutFieldT.USE_CLASS} name="classId" rules={[{ required: true }]}>
        <Select showSearch filterOption={(input: any, option: any) => selectFilterOption(input, option)}>
          {getOptionArray(classDataArray)}
        </Select>
      </Form.Item>

      <Form.Item label={rentOutFieldT.USE} name="description">
        <Input.TextArea rows={4} style={{ width: "100%" }} />
      </Form.Item>

      {handleType === 'update' && hasPermission('RENT_OUT_FIELD_DELETE') &&
        <>
          <Divider dashed />
          <Form.Item label={buttonT.DELETE}>
            <ReButton onClick={clickDelete} attributeObject={{ type: "danger" }} iconName='delete' />
          </Form.Item>
        </>
      }
    </Form>
  );
}

export default ModalInfo;